/**
 * Watching a BZO ingestion run.
 *
 * Deliberately the same shape as the dossier pipeline's progress view: the real
 * stages the backend reports (`dokument_pruefen` → `text_lesen` → `zonen_lesen`
 * → `entwurf_pruefen` → `entwurf_ablegen`), each with the backend's own reason
 * when it was skipped or failed, polled until the work is terminal. A scanned
 * BZO with no text layer fails at the first stage with a plain-German reason
 * rather than quietly producing an empty pack — and this view shows exactly
 * that sentence, not a summary of it.
 *
 * The landing place on success is the draft pack, not a "done" screen: the
 * ingestion produced work for a human, and the next thing the reader needs is
 * the verification queue.
 */
import { ArrowLeft, Check, CircleAlert, CircleDashed, LoaderCircle, MinusCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { NoticeList } from '@/components/NoticeList'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n, type TranslationKey } from '@/i18n'
import { buildPath, navigate } from '@/lib/router'
import { cn } from '@/lib/utils'
import { usePolling } from '@/lib/usePolling'

import { DraftNote } from './DraftBadge'
import { splitPackId } from './rulebase'
import type { RulebaseIngestion, StageState, WorkspaceRef } from '@/api/types'

const STAGE_LOOK: Record<StageState, { icon: LucideIcon; label: TranslationKey; className: string }> = {
  pending: { icon: CircleDashed, label: 'stage.pending', className: 'text-muted-2' },
  running: { icon: LoaderCircle, label: 'stage.running', className: 'text-accent' },
  done: { icon: Check, label: 'stage.done', className: 'text-signal-green-text' },
  skipped: { icon: MinusCircle, label: 'stage.skipped', className: 'text-muted' },
  failed: { icon: CircleAlert, label: 'stage.failed', className: 'text-signal-red-text' },
}

const TERMINAL = new Set(['succeeded', 'failed'])

export function IngestionProgress({
  workspace,
  ingestionId,
}: {
  workspace: WorkspaceRef
  ingestionId: string
}) {
  const { t } = useI18n()
  const { data: ingestion, error } = usePolling<RulebaseIngestion>(
    ingestionId,
    (signal) => api.ingestion(workspace.id, ingestionId, signal),
    (value) => TERMINAL.has(value.status),
  )

  const done = ingestion?.status === 'succeeded'
  // `pack_id` is the composite key (`zh/zollikon`) and goes straight into the
  // pack URL. It is null while the ingestion is queued and after a failure —
  // hence the guard below rather than a link that would point nowhere.
  const packId = ingestion?.pack_id ?? null

  return (
    <div className="flex max-w-[900px] flex-col gap-8">
      <div className="flex flex-col gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => navigate(buildPath('rulebase', { workspaceId: workspace.id }))}
        >
          <Icon icon={ArrowLeft} />
          {t('common.back')}
        </Button>
        <h1 className="font-display text-[32px] leading-tight font-semibold">
          {t('rulebase.ingestionTitle')}
        </h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('rulebase.ingestionLead')}</p>
        <dl className="flex flex-wrap gap-x-8 gap-y-2 text-[13px]">
          <div className="flex gap-2">
            <dt className="text-muted">{t('rulebase.ingestionGemeinde')}</dt>
            <dd className="text-ink-2">{ingestion?.gemeinde ?? t('common.none')}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted">Status</dt>
            <dd className="text-ink-2" data-testid="ingestion-status">
              {ingestion ? t(`run.status.${ingestion.status}` as TranslationKey) : t('common.loading')}
            </dd>
          </div>
        </dl>
      </div>

      {/* Said before the pack is even open: what comes out of here is a draft. */}
      <DraftNote gemeinde={ingestion?.gemeinde ?? null} />

      <NoticeList notices={ingestion?.notices} title={t('notices.runTitle')} />

      {error ? <ErrorNote message={`${t('rulebase.ingestionPollError')} ${error}`} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('rulebase.ingestionStagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol
            className="flex flex-col"
            aria-live="polite"
            aria-busy={ingestion ? !TERMINAL.has(ingestion.status) : true}
          >
            {(ingestion?.stages ?? []).map((stage) => {
              const look = STAGE_LOOK[stage.status]
              return (
                <li
                  key={stage.key}
                  data-stage={stage.key}
                  data-status={stage.status}
                  className="flex items-start gap-3 border-b border-border py-3 last:border-b-0"
                >
                  <Icon
                    icon={look.icon}
                    className={cn(
                      'mt-0.5 size-4',
                      look.className,
                      stage.status === 'running' && 'motion-safe:animate-spin',
                    )}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-[14px] font-medium text-ink">{stage.label_de}</span>
                    {stage.reason ? (
                      <span className="text-[12px] text-muted">{stage.reason}</span>
                    ) : null}
                  </div>
                  <span className="ms-auto shrink-0 text-[12px] text-muted">{t(look.label)}</span>
                </li>
              )
            })}
            {!ingestion ? (
              <li className="py-3 text-[13px] text-muted">{t('common.loading')}</li>
            ) : null}
          </ol>
        </CardContent>
      </Card>

      {ingestion?.status === 'failed' ? (
        <ErrorNote
          message={`${t('rulebase.ingestionFailed')}: ${ingestion.error ?? t('common.error')}`}
        />
      ) : null}

      {done && packId ? (
        <Button
          className="self-start"
          onClick={() => {
            const { kanton, gemeinde } = splitPackId(packId)
            navigate(
              buildPath('rulebasePack', { workspaceId: workspace.id, kanton, gemeinde }),
            )
          }}
        >
          {t('rulebase.ingestionOpenPack')}
        </Button>
      ) : null}
    </div>
  )
}
